import axios, { AxiosError } from 'axios';
import { Api } from '../axiosApi';

// SPED Fiscal (EFD ICMS/IPI) — ver providers/sped no backend. A geração roda
// em segundo plano: `iniciar` devolve o id e `andamento` é consultado
// repetidamente pela tela pra mostrar o passo a passo ao vivo.

export type TStatusEtapaSped = 'aguardando' | 'executando' | 'ok' | 'aviso' | 'erro';
export type TNivelPendenciaSped = 'erro' | 'aviso' | 'info';

export interface IEtapaSped {
  id: string;
  titulo: string;
  descricao: string;
  status: TStatusEtapaSped;
  detalhe?: string;
}

export interface IPendenciaSped {
  id: string;
  nivel: TNivelPendenciaSped;
  area: string;
  titulo: string;
  detalhe: string;
  comoCorrigir: string;
  link?: string;
}

export interface IResumoSped {
  periodo: string;
  regime: string;
  notasSaida: number;
  notasSaidaCanceladas: number;
  nfce: number;
  notasEntrada: number;
  participantes: number;
  produtos: number;
  valorSaidas: number;
  valorEntradas: number;
  icmsDebitos: number;
  icmsCreditos: number;
  icmsARecolher: number;
  saldoCredor: number;
  linhas: number;
  versaoLeiaute: string;
}

export interface IAndamentoSped {
  id: string;
  etapas: IEtapaSped[];
  pendencias: IPendenciaSped[];
  concluido: boolean;
  erroFatal?: string;
  resultado?: {
    nomeArquivo: string;
    arquivoBase64: string;
    resumo: IResumoSped;
  };
}

interface ErrorResponse {
  errors: { default?: string; [campo: string]: string | undefined };
}

const mensagemDeErro = (error: unknown): Error => {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<ErrorResponse>;
    if (axiosError.response) {
      const erros = axiosError.response.data?.errors;
      return new Error(erros?.default || Object.values(erros || {}).filter(Boolean).join(' | ') || 'Erro desconhecido');
    }
    if (axiosError.request) return new Error('Nenhuma resposta do servidor');
    return new Error(axiosError.message);
  }
  return new Error('Erro inesperado');
};

const iniciar = async (mes: string, finalidade: 0 | 1): Promise<string | Error> => {
  try {
    const { data } = await Api.post<{ id: string }>('/sped/efd', { mes, finalidade });
    return data.id;
  } catch (error) {
    return mensagemDeErro(error);
  }
};

const andamento = async (id: string): Promise<IAndamentoSped | Error> => {
  try {
    const { data } = await Api.get<IAndamentoSped>(`/sped/efd/${id}`);
    return data;
  } catch (error) {
    return mensagemDeErro(error);
  }
};

// Baixa o .txt exatamente como veio (ISO-8859-1) — sem passar por string,
// que viraria UTF-8 e estragaria os acentos.
const baixarArquivo = (nomeArquivo: string, arquivoBase64: string) => {
  const binario = atob(arquivoBase64);
  const bytes = new Uint8Array(binario.length);
  for (let i = 0; i < binario.length; i++) bytes[i] = binario.charCodeAt(i);
  const url = URL.createObjectURL(new Blob([bytes], { type: 'text/plain;charset=iso-8859-1' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = nomeArquivo;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
};

export const SpedService = {
  iniciar,
  andamento,
  baixarArquivo,
};
